import { Component, computed, input, model, signal } from '@angular/core';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from 'primeng/tabs';
import { Tag } from 'primeng/tag';
import { CategoryLegalConfig, ContentDocument } from '@/lib-backend';
import {
    LEGAL_DOC_KEYS,
    LEGAL_DOC_LABELS,
    LegalDocKey,
} from '@/app/models/legal-documents';
import { LegalDocView } from './legal-doc-view';
import { LegalDocEditDialog } from './legal-doc-edit-dialog';

/**
 * Category-level editor for the seven legal document templates: one sub-tab
 * per document showing the title and rendered content; editing happens in a
 * popup dialog. The documents authored here are the category defaults realms
 * inherit (and may override per realm); Liquid variables are substituted at
 * serve time.
 */
@Component({
    selector: 'app-category-legal-form',
    standalone: true,
    imports: [
        Tabs,
        Tab,
        TabList,
        TabPanel,
        TabPanels,
        Tag,
        LegalDocView,
        LegalDocEditDialog,
    ],
    templateUrl: './category-legal-form.html',
})
export class CategoryLegalForm {
    readonly value = model.required<CategoryLegalConfig>();
    readonly disabled = input(false);

    protected readonly docKeys = LEGAL_DOC_KEYS;
    protected readonly labels = LEGAL_DOC_LABELS;

    // ── Edit dialog ──────────────────────────────────────────────────────────
    protected readonly editDialogVisible = signal(false);
    protected readonly editingKey = signal<LegalDocKey | null>(null);
    protected readonly editTitle = signal('');
    protected readonly editContent = signal('');

    protected readonly editDialogHeader = computed(() => {
        const key = this.editingKey();
        return key ? `Edit — ${this.labels[key]}` : '';
    });

    protected openEdit(key: LegalDocKey): void {
        const doc = this.value()[key];
        this.editingKey.set(key);
        this.editTitle.set(doc.Title);
        this.editContent.set(doc.Content);
        this.editDialogVisible.set(true);
    }

    protected applyEdit(): void {
        const key = this.editingKey();
        if (key) {
            this.setDoc(key, {
                Title: this.editTitle(),
                Content: this.editContent(),
            });
        }
        this.editingKey.set(null);
    }

    private setDoc(key: LegalDocKey, patch: Partial<ContentDocument>): void {
        this.value.update((v) => ({ ...v, [key]: { ...v[key], ...patch } }));
    }
}
